import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';

import {FlexColumn, Label} from '@forecast-it/design-system';

import DirectApi from '../../../../../../../directApi';
import Dropdown from '../../../../dropdowns/dropdown';

import type {TimeRegistrationModalIntegrations_project$data as TimeRegistrationModalIntegrations_project} from './__generated__/TimeRegistrationModalIntegrations_project.graphql';
import type {TimeRegistrationModalIntegrations_person$data as TimeRegistrationModalIntegrations_person} from './__generated__/TimeRegistrationModalIntegrations_person.graphql';
import type {TimeRegistrationModalIntegrations_company$data as TimeRegistrationModalIntegrations_company} from './__generated__/TimeRegistrationModalIntegrations_company.graphql';

type IntegrationItem = {
	id: string;
	name: string;
};

export type FormattedHarvestTask = {
	value: string;
};

export type FormattedUnit4Activity = {
	value: string;
	label: string;
};

type TimeRegistrationModalIntegrationsProps = {
	project: TimeRegistrationModalIntegrations_project | null;
	person: TimeRegistrationModalIntegrations_person;
	company: TimeRegistrationModalIntegrations_company;
	selectedHarvestTask: FormattedHarvestTask | null;
	handleHarvestTaskChange: (harvestTask: FormattedHarvestTask) => void;
	selectedUnit4Activity: FormattedUnit4Activity | null;
	handleUnit4ActivityChange: (unit4Activity: FormattedUnit4Activity) => void;
};
function TimeRegistrationModalIntegrations({
	project,
	person,
	company,
	selectedHarvestTask,
	handleHarvestTaskChange,
	selectedUnit4Activity,
	handleUnit4ActivityChange,
}: TimeRegistrationModalIntegrationsProps): JSX.Element {
	const {formatMessage} = useIntl();

	const [harvestTasks, setHarvestTasks] = useState<IntegrationItem[]>([]);
	const [harvestLoading, setHarvestLoading] = useState(false);
	const [unit4Activities, setUnit4Activities] = useState<IntegrationItem[]>([]);
	const [unit4Loading, setUnit4Loading] = useState(false);

	const prevProjectId = useRef<string | null>(project ? project.id : null);

	function fetchHarvestTasks(project) {
		//If a project is selected, show only tasks that belong to that project
		if (project && project.harvestProject) {
			if (harvestLoading === true || harvestTasks.length > 0) return;
			setHarvestLoading(true);
			DirectApi.Fetch('harvest_tasks/' + project.id).then(tasks => {
				setHarvestTasks(tasks);
				setHarvestLoading(false);
			});
		} else {
			setHarvestTasks([]);
			setHarvestLoading(false);
		}
	}

	function fetchUnit4Activities(project) {
		//If a project is selected, show only tasks that belong to that project
		if (project && project.unit4Project) {
			if (unit4Loading === true || unit4Activities.length > 0) return;
			setUnit4Loading(true);
			DirectApi.Fetch('unit4_activities/' + project.id).then(activities => {
				setUnit4Activities(activities);
				setUnit4Loading(false);
			});
		} else {
			setUnit4Activities([]);
			setUnit4Loading(false);
		}
	}

	useEffect(() => {
		if (project && prevProjectId.current !== project.id) {
			setUnit4Activities([]);
			setHarvestTasks([]);

			prevProjectId.current = project.id;
		}
	}, [project]);

	useEffect(() => {
		if (selectedHarvestTask && project) {
			fetchHarvestTasks(project);
		}
	}, []);

	const harvestTaskOptions: FormattedHarvestTask[] = harvestTasks
		? harvestTasks.map(harvestTask => {
				return {value: harvestTask.id, label: harvestTask.name};
		  })
		: [];

	const unit4ActivityOptions: FormattedUnit4Activity[] = unit4Activities
		? unit4Activities.map(unit4Activity => {
				return {value: unit4Activity.id, label: unit4Activity.name};
		  })
		: [];

	const showHarvestDropdown = project && project.harvestProject && person.harvestUser && company.harvestEnabled;
	const showUnit4Dropdown =
		project && project.unit4Project && project.unit4Project.activitiesEnabled && person.unit4User && company.unit4Enabled;

	return (
		<>
			{showHarvestDropdown ? (
				<FlexColumn gap={'s'}>
					<Label>Harvest task</Label>
					<Dropdown
						id={'time-harvest-dropdown'}
						options={harvestTaskOptions}
						onChange={handleHarvestTaskChange}
						value={selectedHarvestTask ? selectedHarvestTask.value : ''}
						multi={false}
						clearable={true}
						focusOnClick={true}
						customHeight={30}
						placeholder={
							harvestLoading
								? formatMessage({id: 'common.loading'})
								: formatMessage({id: 'card_modal.harvest_task_placeholder'})
						}
						maxHeight={245}
						onClick={() => fetchHarvestTasks(project)}
						integrationDropdown={true}
						integrationLoading={harvestLoading}
						integrationRetryToFetch={() => fetchHarvestTasks(project)}
						hideLabel
					/>
				</FlexColumn>
			) : null}
			{showUnit4Dropdown ? (
				<FlexColumn gap={'s'}>
					<Label>Unit4 activity</Label>
					<Dropdown
						id={'time-unit4-dropdown'}
						options={unit4ActivityOptions}
						onChange={handleUnit4ActivityChange}
						value={selectedUnit4Activity ? selectedUnit4Activity.value : ''}
						multi={false}
						clearable={true}
						focusOnClick={true}
						customHeight={30}
						placeholder={formatMessage({id: 'integrations.unit4_activity_placeholder'})}
						maxHeight={245}
						onClick={() => fetchUnit4Activities(project)}
						integrationDropdown={true}
						integrationLoading={unit4Loading}
						integrationRetryToFetch={() => fetchUnit4Activities(project)}
						hideLabel
					/>
				</FlexColumn>
			) : null}
		</>
	);
}

export default createFragmentContainer(TimeRegistrationModalIntegrations, {
	project: graphql`
		fragment TimeRegistrationModalIntegrations_project on ProjectType {
			id
			harvestProject {
				id
			}
			unit4Project {
				activitiesEnabled
			}
		}
	`,
	person: graphql`
		fragment TimeRegistrationModalIntegrations_person on Person {
			harvestUser
			unit4User
		}
	`,
	company: graphql`
		fragment TimeRegistrationModalIntegrations_company on Company {
			harvestEnabled
			unit4Enabled
		}
	`,
});
